import * as React from "react";
import { LineChart } from "@mui/x-charts/LineChart";
import { useTranslation } from "react-i18next";

function CustomTick({ x, y, value, range }) {
  const d = value instanceof Date ? value : new Date(value);

  let label;
  if (["1H", "3H", "6H", "12H", "1D"].includes(range)) {
    label = d.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/Istanbul",
    });
  } else {
    label = d.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
      timeZone: "Europe/Istanbul",
    });
  }

  return (
    <text x={x} y={y} textAnchor="middle" fontSize={11} fill="#444">
      {label}
    </text>
  );
}

export default function ActivePowerChart({ data, range, onRangeChange, isVirtual, deviceType }) {
  const { t } = useTranslation();
  const now = new Date();
  const showMarks = ["1H", "3H"].includes(range);

  let min;
  switch (range) {
    case "1H": min = new Date(now.getTime() - 1 * 60 * 60 * 1000); break;
    case "3H": min = new Date(now.getTime() - 3 * 60 * 60 * 1000); break;
    case "6H": min = new Date(now.getTime() - 6 * 60 * 60 * 1000); break;
    case "12H": min = new Date(now.getTime() - 12 * 60 * 60 * 1000); break;
    case "1D": min = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000); break;
    case "3D": min = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000); break;
    case "1W": min = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
    default: min = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }
  const max = now;

  // Grup Arge'de toplam import/export yok
  const showTotals = deviceType !== "gruparge";

  // Verileri kW cinsine çevir (1000'e böl)
  const processedData = React.useMemo(() => {
    return data.map((d) => ({
      ...d,
      Pmean1: d.Pmean1 != null ? d.Pmean1 / 1000 : null,
      Pmean2: d.Pmean2 != null ? d.Pmean2 / 1000 : null,
      Pmean3: d.Pmean3 != null ? d.Pmean3 / 1000 : null,
      PTotalImport: d.PTotalImport != null ? d.PTotalImport / 1000 : null,
      PTotalExport: d.PTotalExport != null ? d.PTotalExport / 1000 : null,
    }));
  }, [data]);

  return (
    <div>
      {/* Filtre Butonları */}
      <div className="flex flex-wrap gap-2 mb-4 justify-center md:justify-start">
        {[
          { key: "1H", label: t('timeRanges.1H') },
          { key: "3H", label: t('timeRanges.3H') },
          { key: "6H", label: t('timeRanges.6H') },
          { key: "12H", label: t('timeRanges.12H') },
          { key: "1D", label: t('timeRanges.1D') },
          { key: "3D", label: t('timeRanges.3D') },
          { key: "1W", label: t('timeRanges.1W') },
        ].map((r) => (
          <button
            key={r.key}
            onClick={() => onRangeChange(r.key)}
            className={`px-3 py-2 rounded-md border text-sm font-medium transition
              ${range === r.key
                ? "bg-red-600 text-white border-red-600"
                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <LineChart
        dataset={processedData}
        margin={{ left: 50, right: 30, top: 20, bottom: 30 }}
        xAxis={[
          {
            dataKey: "time",
            scaleType: "time",
            position: "bottom",
            min: min.getTime(),
            max: max.getTime(),
            tickNumber: 8,
            tickLabel: (props) => <CustomTick {...props} range={range} />,
            valueFormatter: (value) => {
              const d = value instanceof Date ? value : new Date(value);
              if (["1H", "3H", "6H", "12H", "1D"].includes(range)) {
                return d.toLocaleTimeString("tr-TR", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                  timeZone: "Europe/Istanbul",
                });
              } else {
                return d.toLocaleDateString("tr-TR", {
                  day: "2-digit",
                  month: "short",
                  timeZone: "Europe/Istanbul",
                });
              }
            },
          },
        ]}
        yAxis={[
          {
            label: "kW",
            valueFormatter: (v) => `${v.toLocaleString("tr-TR")}`,
          },
        ]}

        series={
          isVirtual
            ? [
              {
                dataKey: "PTotalImport",
                label: t('chartLabels.totalImportActivePower'),
                color: "#16a34a",
                showLine: true,
                showMark: showMarks,
                valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kW` : "-")
              },
              {
                dataKey: "PTotalExport",
                label: t('chartLabels.totalExportActivePower'),
                color: "#6366f1",
                showLine: true,
                showMark: showMarks,
                valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kW` : "-")
              },
            ]
            : [
              {
                dataKey: "Pmean1",
                label: t('chartLabels.L1NActivePower'),
                color: "#e63946",
                showLine: true,
                showMark: showMarks,
                valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kW` : "-")
              },
              {
                dataKey: "Pmean2",
                label: t('chartLabels.L2NActivePower'),
                color: "#2a9d8f",
                showLine: true,
                showMark: showMarks,
                valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kW` : "-")
              },
              {
                dataKey: "Pmean3",
                label: t('chartLabels.L3NActivePower'),
                color: "#1d3557",
                showLine: true,
                showMark: showMarks,
                valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kW` : "-")
              },
              ...(showTotals ? [
                {
                  dataKey: "PTotalImport",
                  label: t('chartLabels.totalImportActivePower'),
                  color: "#16a34a",
                  showLine: true,
                  showMark: showMarks,
                  valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kW` : "-")
                },
                {
                  dataKey: "PTotalExport",
                  label: t('chartLabels.totalExportActivePower'),
                  color: "#6366f1",
                  showLine: true,
                  showMark: showMarks,
                  valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kW` : "-")
                },
              ] : []),
            ]
        }

        height={400}
        axisHighlight={{ x: "line" }}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'top', horizontal: 'middle' },
            padding: 0,
          },
          tooltip: { trigger: "axis" },
        }}
      />
    </div>
  );
}
